import { type ComponentType, lazy } from 'react';

type LazyFactory<T extends ComponentType<unknown>> = () => Promise<{ default: T }>;

const RELOAD_KEY = 'chunk-reload-ts';
const RELOAD_COOLDOWN_MS = 10_000;
const INLINE_RETRY_MAX = 2;
const INLINE_RETRY_DELAY_MS = 400;

function isChunkFetchError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return (
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Loading chunk') ||
    error.message.includes('ChunkLoadError') ||
    error.message.includes('error loading dynamically imported module') ||
    error.name === 'ChunkLoadError'
  );
}

/**
 * Lazy-load a route module with defense-in-depth retry.
 *
 * Layer 1 (in-session): retry the dynamic import up to 2x with 400ms backoff.
 *   Catches transient Vite HMR-graph invalidations in dev and CDN hiccups in
 *   prod without nuking component state.
 * Layer 2 (cross-session): if retries still fail, hard-reload the page once
 *   per 10s cooldown. In dev this rebuilds the module graph; in prod this
 *   picks up the latest deploy's chunk mapping. Cooldown prevents reload
 *   loops if the actual bundle is broken (we'd rather surface an error than
 *   reload forever).
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(factory: LazyFactory<T>) {
  return lazy(async () => {
    let lastError: unknown;
    for (let attempt = 0; attempt <= INLINE_RETRY_MAX; attempt++) {
      try {
        return await factory();
      } catch (err) {
        lastError = err;
        if (!isChunkFetchError(err) || attempt === INLINE_RETRY_MAX) break;
        await new Promise((r) => setTimeout(r, INLINE_RETRY_DELAY_MS * (attempt + 1)));
      }
    }

    if (isChunkFetchError(lastError)) {
      const lastReload = Number(sessionStorage.getItem(RELOAD_KEY) || '0');
      const now = Date.now();
      if (now - lastReload > RELOAD_COOLDOWN_MS) {
        sessionStorage.setItem(RELOAD_KEY, String(now));
        window.location.reload();
        return new Promise<{ default: T }>(() => {});
      }
    }
    throw lastError;
  });
}
